import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { SharerHub } from 'src/app/models/SharedHub';
import { AccordionDataService, AccordionFolder } from '../../services/accordion-data.service';
import {
  SharedUsersDialogComponent,
  SharedUsersDialogData
} from '../shared-users-dialog/shared-users-dialog.component';

/**
 * Home Folders Accordion Component with Pagination Support
 *
 * This component displays folders in an accordion format with expandable file lists.
 * It now includes pagination support to handle large numbers of files efficiently.
 *
 * Features:
 * - Paginated file display (default: 20 items per page, max: 50)
 * - Page size selection (10, 20, 30, 40, 50 items)
 * - Navigation controls (first, previous, next, last page)
 * - Responsive design for mobile devices
 * - File selection across all pages
 * - Maintains selection state when navigating between pages
 *
 * Usage:
 * The parent component should provide the filesLoaded input when files are loaded:
 * <app-home-folders-accordion
 *   [filesLoaded]="filesLoadedData"
 *   ... other inputs ...
 * >
 */

const DICOMDIR_FILE_NAME = 'DICOMDIR';

export interface HomeFolder {
  name: string;
  id: string;
  color?: string;
  children?: any[];
  hasNotifications?: boolean;
  count?: string;
  type?: string;
  expanded?: boolean;
  loading?: boolean;
  files?: any[];
  restricted_to?: any[];
}

export interface FolderFile {
  fileName: string;
  fileDesc?: string;
  lastModified?: string;
  uploadedDate?: string;
  fdate?: string;
  ftime?: string;
  type?: string;
  fileId: string;
  hasNotifications?: boolean;
  hasCommentAttachments?: boolean; // Whether this file has comment attachments
  sharedUsers?: any[];
  parentFolderName?: string;
  parentFolder?: string;
  color?: string;
  viewerurl?: string;
  ftype?: string;
  filePath?: string;
  storename?: string;
  style?: any; // CSS styles object for ngStyle binding
  folderId?: string; // ID of the folder containing this file
  predefined?: boolean; // Whether this file is predefined/system file
  fileSize?: number; // File size in bytes
}

// Pagination interface
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

@Component({
    selector: 'app-home-folders-accordion-facilitators',
    templateUrl: './home-folders-accordion-facilitators.component.html',
    styleUrls: ['./home-folders-accordion-facilitators.component.scss'],
    standalone: false
})
export class HomeFoldersAccordionFacilitatorsComponent implements OnInit, OnDestroy, OnChanges {
  // Simplified inputs for HUB-only functionality
  @Input() permissions: any = { createfolder: false, deletefiles: false, editfiles: false, sharefileicon: false };
  @Input() loading: boolean = false;
  @Input() loadingMessage: string = '';
  @Input() casename: string = '';
  @Input() selectedHub: any = null;
  @Input() isSpoke: boolean = false;
  @Input() showHubsAsFolders: boolean = false;
  @Input() sharerHubs: SharerHub[] = [];

  // Optional: Keep these for backward compatibility if needed
  @Input() folders: HomeFolder[] = [];
  @Input() currentFolderName: string = '';

  // Essential outputs for HUB functionality
  @Output() hubClick = new EventEmitter<any>();
  @Output() loadFolderContents = new EventEmitter<string>();
  @Output() refreshFolderContents = new EventEmitter<string>();
  @Output() fileClick = new EventEmitter<FolderFile>();
  @Output() editFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() deleteFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() copyFile = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() generateDisc = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() addCommentOrAttachment = new EventEmitter<{ file: FolderFile; event: Event }>();
  @Output() sharedIconClick = new EventEmitter<FolderFile>();
  @Output() folderNameClick = new EventEmitter<FolderFile>();
  @Output() studyClick = new EventEmitter<FolderFile>();
  @Output() folderSelectionChange = new EventEmitter<{ folderId: string; selectedFiles: FolderFile[] }>();
  @Output() allFoldersClosed = new EventEmitter<void>();

  // Optional: Keep these for backward compatibility if needed
  @Output() folderClick = new EventEmitter<HomeFolder>();
  @Output() createFolder = new EventEmitter<void>();
  @Output() deleteFolder = new EventEmitter<{ folder: AccordionFolder; event: Event }>();

  accordionFolders: AccordionFolder[] = [];
  private subscription: Subscription = new Subscription();

  // New property to track hierarchical display
  displayFolders: AccordionFolder[] = [];

  titleText: string = 'Shared files from';

  // Store regular folders for HUB expansion
  private regularFolders: any[] = [];

  // ViewChildren for folder elements to enable scrolling
  @ViewChildren('folderItem') folderItems!: QueryList<ElementRef>;

  // File selection tracking for each folder
  private folderSelections: Map<string, Set<string>> = new Map();

  // Pagination properties
  private paginationStates: Map<string, PaginationState> = new Map();
  readonly defaultPageSize = 5;
  readonly maxPageSize = 50;

  // Subfolder expansion tracking
  private subfolderExpansionStates: Map<string, boolean> = new Map();

  // Tab selection tracking for hub folders
  private tabSelectionStates: Map<string, number> = new Map();

  // Make Math available in template
  Math = Math;

  constructor(
    private accordionDataService: AccordionDataService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Subscribe to folder data changes
    this.subscription.add(
      this.accordionDataService.folders$.subscribe(folders => {
        // Filter to show only HUB folders initially
        const hubFoldersOnly = folders.filter(folder => folder.type === 'hub');
        this.accordionFolders = hubFoldersOnly;
        this.updateDisplayFolders();
        this.initializePaginationForFolders(hubFoldersOnly);

        // Update pagination state for folders that have files
        hubFoldersOnly.forEach(folder => {
          if (folder.files && folder.files.length > 0) {
            this.updatePaginationState(folder.id, folder.files.length);
          }
        });

        // Ensure hub folders are present if they're missing
        const hasHubFolders = hubFoldersOnly.length > 0;
        if (!hasHubFolders && this.isSpoke && this.showHubsAsFolders && this.sharerHubs && this.sharerHubs.length > 0) {
          this.ensureHubFoldersPresent();
        }
      })
    );

    // Initialize ONLY HUB folders for spoke users
    if (this.isSpoke && this.showHubsAsFolders && this.sharerHubs && this.sharerHubs.length > 0) {
      this.initializeHubFolders();
    }

    // Ensure hub folders are present after initialization
    this.ensureHubFoldersPresent();
  }

  // Removed empty ngAfterViewInit lifecycle method to comply with linter rules

  /**
   * Store regular folders for HUB expansion
   */
  private storeRegularFolders(folders: any[]): void {
    this.regularFolders = folders;
  }

  /**
   * Initialize HUB folders for spoke users
   */
  private initializeHubFolders(): void {
    const hubFolders = this.sharerHubs.map((hub, index) => ({
      id: `hub-${index}`,
      name: typeof hub === 'string' ? hub : hub.name || hub.name || `Hub ${index + 1}`,
      color: '#2196F3', // Blue color for hub folders
      hasNotifications: false,
      count: '',
      type: 'hub',
      expanded: false,
      loading: false,
      refreshing: false,
      files: null,
      hubData: hub, // Store the original hub data
      restrictedTo: null
    }));

    // Set hub folders in the accordion data service
    this.accordionDataService.setFolders(hubFolders);
    this.initializePaginationForFolders(hubFolders);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for changes to the selectedHub
    if (changes['selectedHub']) {
      // When hub changes, re-apply facility filtering to existing folders
      this.applyFacilityFiltering();

      // For spoke users with showHubsAsFolders, also re-initialize if we don't have hub folders yet
      if (this.isSpoke && this.showHubsAsFolders && this.sharerHubs && this.sharerHubs.length > 0) {
        const currentFolders = this.accordionDataService.getCurrentFolders();
        const hasHubFolders = currentFolders.some(folder => folder.type === 'hub');
        if (!hasHubFolders) {
          this.initializeHubFolders();
        }
      }
    }

    // Watch for changes to sharerHubs (for spoke users with showHubsAsFolders)
    if (changes['sharerHubs'] && this.isSpoke && this.showHubsAsFolders) {
      this.initializeHubFolders();
    }

    // Watch for changes to showHubsAsFolders
    if (changes['showHubsAsFolders']) {
      this.initializeHubFolders();
    }

    // Watch for changes to the folders input property
    if (changes['folders'] && changes['folders'].currentValue) {
      // Store the regular folders for when HUBs are expanded
      this.storeRegularFolders(changes['folders'].currentValue);
      this.ensureHubFoldersPresent();
      this.updateDisplayFolders();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Ensure hub folders are present in the accordion data service
   * This method is called to guarantee hub folders exist when needed
   */
  /**
   * Store hub folder states to preserve expansion state across updates
   */
  private hubFolderStates: Map<string, { expanded: boolean; children: any[] }> = new Map();

  private ensureHubFoldersPresent(): void {
    if (this.isSpoke && this.showHubsAsFolders && this.sharerHubs && this.sharerHubs.length > 0) {
      const currentFolders = this.accordionDataService.getCurrentFolders();
      const existingHubFolders = currentFolders.filter(folder => folder.type === 'hub');
      const hasHubFolders = existingHubFolders.length > 0;

      // Save current hub folder states before potentially recreating them
      if (hasHubFolders) {
        existingHubFolders.forEach(hub => {
          this.hubFolderStates.set(hub.id, {
            expanded: hub.expanded || false,
            children: hub.children || []
          });
        });
      }

      if (!hasHubFolders) {
        // Create hub folders and restore their previous state if it exists
        const hubFolders = this.sharerHubs.map((hub, index) => {
          const hubId = `hub-${index}`;
          const savedState = this.hubFolderStates.get(hubId);

          return {
            id: hubId,
            name: typeof hub === 'string' ? hub : hub.name || hub.name || `Hub ${index + 1}`,
            color: '#2196F3', // Blue color for hub folders
            hasNotifications: false,
            count: '',
            type: 'hub',
            expanded: savedState ? savedState.expanded : false, // Restore expansion state
            loading: false,
            refreshing: false,
            files: null,
            children: savedState ? savedState.children : [],
            hubData: hub, // Store the original hub data
            restrictedTo: null
          };
        });

        // For HUB-only functionality, set ONLY hub folders (no regular folders)
        this.accordionDataService.setFolders(hubFolders);
      }
    }
  }

  // Removed protectIndexFolderCount method - not needed for HUB-only functionality

  private updateFolderDataOnly(newFolders: any[]): void {
    // Simplified for HUB-only functionality
    // Just ensure hub folders are preserved during updates
    this.ensureHubFoldersPresent();
    this.updateDisplayFolders();
  }

  private initializeAccordionFolders(): void {
    // Simplified for HUB-only functionality
    // Just initialize hub folders
    this.initializeHubFolders();
  }

  // Pagination methods
  private initializePaginationForFolders(folders: AccordionFolder[]): void {
    folders.forEach(folder => {
      if (!this.paginationStates.has(folder.id)) {
        this.paginationStates.set(folder.id, {
          currentPage: 1,
          pageSize: this.defaultPageSize,
          totalItems: 0,
          totalPages: 0
        });
      }
    });
  }

  private updatePaginationState(folderId: string, totalItems: number): void {
    const state = this.paginationStates.get(folderId) || {
      currentPage: 1,
      pageSize: this.defaultPageSize,
      totalItems: 0,
      totalPages: 0
    };

    state.totalItems = totalItems;
    state.totalPages = Math.ceil(totalItems / state.pageSize);

    // Ensure current page is within bounds
    if (state.currentPage > state.totalPages && state.totalPages > 0) {
      state.currentPage = state.totalPages;
    }

    this.paginationStates.set(folderId, state);
  }

  getPaginationState(folderId: string): PaginationState {
    return (
      this.paginationStates.get(folderId) || {
        currentPage: 1,
        pageSize: this.defaultPageSize,
        totalItems: 0,
        totalPages: 0
      }
    );
  }

  getPaginatedFiles(folder: AccordionFolder): FolderFile[] {
    if (!folder.files || folder.files.length === 0) {
      return [];
    }

    const state = this.getPaginationState(folder.id);
    const startIndex = (state.currentPage - 1) * state.pageSize;
    const endIndex = startIndex + state.pageSize;

    return folder.files.slice(startIndex, endIndex);
  }

  onPageChange(folderId: string, page: number): void {
    const state = this.paginationStates.get(folderId);
    if (state && page >= 1 && page <= state.totalPages) {
      state.currentPage = page;
      this.paginationStates.set(folderId, state);
    }
  }

  onPageSizeChange(folderId: string, pageSize: number): void {
    const state = this.paginationStates.get(folderId);
    if (state) {
      state.pageSize = Math.min(Math.max(pageSize, 10), this.maxPageSize);
      state.currentPage = 1; // Reset to first page
      state.totalPages = Math.ceil(state.totalItems / state.pageSize);
      this.paginationStates.set(folderId, state);
    }
  }

  getPageSizeOptions(): number[] {
    return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  }

  onFolderClick(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Handle hub folders differently
    if (folder.type === 'hub') {
      this.handleHubFolderClick(folder, event);
      return;
    }

    // Check if this folder has children - if so, toggle children visibility
    if (this.hasChildren(folder)) {
      this.toggleChildrenVisibility(folder);
      // Don't return here - allow the folder to also load its files
    }

    // Consultant and facilitator folders are no longer supported

    // Check if we're about to expand (before toggling)
    const willExpand = !folder.expanded;

    // Toggle expansion state
    this.accordionDataService.toggleFolderExpansion(folder.id);

    // Update display folders to reflect the new expansion state
    this.updateDisplayFolders();

    // Only emit folder click event when opening a folder (not when closing)
    if (willExpand) {
      // Emit folder click event to parent component to update currentFolderName
      this.folderClick.emit({
        name: folder.name,
        id: folder.id,
        color: folder.color,
        hasNotifications: folder.hasNotifications,
        count: folder.count,
        type: folder.type,
        expanded: willExpand,
        loading: folder.loading,
        files: folder.files
      });

      // If expanding and no files loaded yet, load from cache or backend
      if (!folder.files && !folder.loading) {
        // First try to get files from cache
        const cachedFiles = this.accordionDataService.getCachedFiles(folder.id);

        if (cachedFiles) {
          // Use cached files
          this.accordionDataService.updateFolderFiles(folder.id, cachedFiles);
          // Update pagination state for cached files
          this.updatePaginationState(folder.id, cachedFiles.length);
        } else {
          // Load from backend if not in cache
          this.accordionDataService.setFolderLoading(folder.id, true);
          this.loadFolderContents.emit(folder.id);
        }
      }
    } else {
      // When closing a folder, check if all folders are now closed and emit event if needed
      // Add a delay to ensure the state has been fully updated
      setTimeout(() => {
        this.checkAndEmitAllFoldersClosed();
      }, 100);
    }

    // Scroll to the expanded folder if it was just opened
    if (willExpand) {
      setTimeout(() => {
        this.scrollToFolder(folder.id);
      }, 100); // Small delay to ensure the DOM has updated
    }
  }

  /**
   * Handle hub folder click - emit hub selection event and implement accordion behavior
   * @param folder The hub folder that was clicked
   * @param event The click event
   */
  private handleHubFolderClick(folder: AccordionFolder, event: Event): void {
    // Check if this hub is already selected
    const isCurrentlySelected = this.isHubSelected(folder);

    if (isCurrentlySelected) {
      // If clicking on the already selected hub, deselect it
      this.accordionDataService.toggleFolderExpansion(folder.id);

      // Clear the saved state for this hub
      this.hubFolderStates.set(folder.id, {
        expanded: false,
        children: []
      });

      this.updateDisplayFolders();
      // Emit null to indicate no hub is selected
      this.hubClick.emit(null);
    } else {
      // For hub folders, we want accordion behavior - only one hub can be selected at a time
      // First, close all other hub folders
      this.closeAllHubFolders();

      // Then expand the clicked hub folder
      this.accordionDataService.toggleFolderExpansion(folder.id);

      // Save the expanded state immediately
      this.hubFolderStates.set(folder.id, {
        expanded: true,
        children: []
      });

      // Get the updated folder from the service after expansion
      const updatedFolders = this.accordionDataService.getCurrentFolders();
      const expandedHubFolder = updatedFolders.find(f => f.id === folder.id);

      if (expandedHubFolder) {
        // Update the hub folder structure to include regular folders as children
        this.updateHubFolderStructure(expandedHubFolder);
      }

      // Update display folders to reflect the new expansion state
      this.updateDisplayFolders();

      // Emit hub click event to parent component
      this.hubClick.emit(folder.hubData);
    }
  }

  /**
   * Close all hub folders to implement accordion behavior
   */
  private closeAllHubFolders(): void {
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const hubFolders = currentFolders.filter(folder => folder.type === 'hub');

    hubFolders.forEach(hubFolder => {
      if (hubFolder.expanded) {
        this.accordionDataService.toggleFolderExpansion(hubFolder.id);
        // Clear children when closing hub folder
        this.clearHubFolderChildren(hubFolder);
      }
    });
  }

  /**
   * Check if a folder allows file uploads
   * @param folder The folder to check
   * @returns true if the folder allows uploads
   */
  canUploadToFolder(folder: AccordionFolder): boolean {
    // If upload_folder is explicitly set, use that value
    if (folder.upload_folder !== undefined) {
      return folder.upload_folder;
    }

    // Default behavior: allow uploads unless it's a system folder
    return !this.isPredefinedFolder(folder);
  }

  /**
   * Update hub folder structure to include regular folders as children
   * @param selectedHub The hub folder that was selected
   */
  private updateHubFolderStructure(selectedHub: AccordionFolder): void {
    // Use the stored regular folders instead of looking in accordion data service
    const allRegularFolders = this.regularFolders || [];

    // Convert regular folders to accordion folder format
    const accordionRegularFolders = allRegularFolders.map(folder => ({
      id: folder.id,
      name: folder.name,
      color: folder.color,
      hasNotifications: folder.hasNotifications,
      count: folder.count,
      type: folder.type || 'folder',
      expanded: false,
      loading: false,
      refreshing: false,
      files: null,
      parentId: null,
      level: 0,
      children: [],
      restrictedTo: folder.restricted_to || folder.restrictedTo,
      upload_folder: folder.upload_folder || false
    }));

    // Filter folders that are visible for this hub
    const candidateChildren = accordionRegularFolders.filter(f =>
      this.isFolderVisibleForSelectedHub({ ...f, type: 'folder' } as any)
    );

    // Create children folders with proper structure
    const childrenFolders = candidateChildren.map(folder => ({
      ...folder,
      parentId: selectedHub.id,
      level: 1, // Children are level 1
      expanded: false, // Children start closed
      loading: false,
      refreshing: false,
      files: folder.files || null,
      children: [], // Children don't have their own children
      upload_folder: folder.upload_folder || false
    }));

    // Add the children folders to the accordion data service
    childrenFolders.forEach(childFolder => {
      this.accordionDataService.updateFolder(childFolder);
    });

    // Update the hub folder to include children
    const updatedHubFolder = {
      ...selectedHub,
      children: childrenFolders,
      expanded: true // Ensure the hub is expanded
    };
    this.accordionDataService.updateFolder(updatedHubFolder);

    // Auto-load files for all child folders when a hub is expanded
    childrenFolders.forEach(childFolder => {
      if (!childFolder.files && !childFolder.loading) {
        const cachedFiles = this.accordionDataService.getCachedFiles(childFolder.id);
        if (cachedFiles) {
          this.accordionDataService.updateFolderFiles(childFolder.id, cachedFiles);
          this.updatePaginationState(childFolder.id, cachedFiles.length);
        } else {
          this.accordionDataService.setFolderLoading(childFolder.id, true);
          this.loadFolderContents.emit(childFolder.id);
        }
      }
    });
  }

  /**
   * Clear children from a hub folder when it's closed
   * @param hubFolder The hub folder to clear children from
   */
  private clearHubFolderChildren(hubFolder: AccordionFolder): void {
    // If the hub folder has children, clear their parentId first
    if (hubFolder.children && hubFolder.children.length > 0) {
      hubFolder.children.forEach(child => {
        const updatedChild = {
          ...child,
          parentId: null,
          level: 0
        };
        this.accordionDataService.updateFolder(updatedChild);
      });
    }

    const updatedHubFolder = {
      ...hubFolder,
      children: [],
      expanded: false
    };

    this.accordionDataService.updateFolder(updatedHubFolder);

    // After clearing hub children, re-apply facility filtering to restore proper folder structure
    this.applyFacilityFiltering();
  }

  /**
   * Check if a hub folder is currently selected (expanded)
   * @param folder The hub folder to check
   * @returns true if the hub folder is currently selected/expanded
   */
  isHubSelected(folder: AccordionFolder): boolean {
    if (folder.type !== 'hub') {
      return false;
    }

    return this.isExpanded(folder);
  }

  /**
   * Get the currently selected hub folder
   * @returns The currently selected hub folder or null if none is selected
   */
  getSelectedHubFolder(): AccordionFolder | null {
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const hubFolders = currentFolders.filter(folder => folder.type === 'hub');

    return hubFolders.find(hubFolder => hubFolder.expanded) || null;
  }

  /**
   * Programmatically select a hub folder by its hub data
   * @param hubData The hub data to select
   */
  selectHubByData(hubData: any): void {
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const hubFolder = currentFolders.find(folder => folder.type === 'hub' && folder.hubData === hubData);

    if (hubFolder) {
      // Close all other hub folders first
      this.closeAllHubFolders();

      // Select the specified hub
      this.accordionDataService.toggleFolderExpansion(hubFolder.id);
      this.updateDisplayFolders();
    }
  }

  /**
   * Update folders while preserving hub folders and their state
   * This method should be called when the parent component wants to update folder data
   * but preserve the hub folder structure and selection state
   * @param newFolders The new folder data from the parent component
   */
  updateFoldersPreservingHubs(newFolders: any[]): void {
    // Get current hub folders and their state
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const hubFolders = currentFolders.filter(folder => folder.type === 'hub');

    // Update the regular folders with new data
    this.updateFolderDataOnly(newFolders);

    // Ensure hub folders are still present after the update
    const updatedFolders = this.accordionDataService.getCurrentFolders();
    const hasHubFolders = updatedFolders.some(folder => folder.type === 'hub');

    if (!hasHubFolders && hubFolders.length > 0) {
      const allFolders = [...updatedFolders, ...hubFolders];
      this.accordionDataService.setFolders(allFolders);
    }

    // Update display folders
    this.updateDisplayFolders();
  }

  /**
   * Scrolls to the specified folder to make it visible at the top
   * @param folderId The ID of the folder to scroll to
   */
  private scrollToFolder(folderId: string): void {
    if (!this.folderItems) {
      return;
    }

    // Find the folder element by its index in the accordionFolders array
    const folderIndex = this.accordionFolders.findIndex(f => f.id === folderId);
    if (folderIndex === -1) {
      return;
    }

    // Get the folder element
    const folderElements = this.folderItems.toArray();
    if (folderIndex >= folderElements.length) {
      return;
    }

    const folderElement = folderElements[folderIndex];
    if (folderElement && folderElement.nativeElement) {
      // Scroll the folder to the top with smooth behavior
      folderElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }

  onRefreshFolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Set refreshing state
    this.accordionDataService.setFolderRefreshing(folder.id, true);

    // Update display folders to reflect the refreshing state
    this.updateDisplayFolders();

    // Emit refresh event to parent component
    this.refreshFolderContents.emit(folder.id);
  }

  onCreateFolder(): void {
    this.createFolder.emit();
  }

  getFolderIcon(folder: AccordionFolder): string {
    if (folder.type === 'search-results') {
      return 'search';
    }
    if (folder.type === 'hub') {
      return 'folder_special';
    }
    if (false) {
      // Consultant folders are no longer supported
      return 'person';
    }
    if (false) {
      // Facilitator folders are no longer supported
      return 'business';
    }
    if (folder.hasNotifications) {
      return 'notifications';
    }
    return 'folder';
  }

  getFolderColor(folder: AccordionFolder): string {
    return folder.color || '#757575';
  }

  /**
   * Get the display name for a folder, including email address for individual folders
   * @param folder The folder to get display name for
   * @returns The display name with email if applicable
   */
  getFolderDisplayName(folder: AccordionFolder): string {
    // Consultant and facilitator folders are no longer supported

    // For individual user folders (those with email in the name pattern), show as is
    if (folder.name && folder.name.includes('@') && folder.name.includes(' - ')) {
      return folder.name; // Already contains email in format "Name - email@domain.com"
    }

    // For regular folders, just return the name
    return folder.name;
  }

  isExpanded(folder: AccordionFolder): boolean {
    // First check if the folder has an expanded property (from displayFolders)
    if (folder.expanded !== undefined) {
      return folder.expanded;
    }

    // Fallback: Get the current state from the accordion data service
    const currentFolders = this.accordionDataService.getCurrentFolders();
    const currentFolder = currentFolders.find(f => f.id === folder.id);
    const result = currentFolder ? currentFolder.expanded || false : false;
    return result;
  }

  areAllFoldersClosed(): boolean {
    // Get the current state from the accordion data service
    const currentFolders = this.accordionDataService.getCurrentFolders();
    return currentFolders.every(folder => !folder.expanded);
  }

  private checkAndEmitAllFoldersClosed(): void {
    if (this.areAllFoldersClosed()) {
      this.allFoldersClosed.emit();
    }
  }

  isRefreshing(folder: AccordionFolder): boolean {
    return this.accordionDataService.isFolderRefreshing(folder.id);
  }

  onFileClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    // Emit file click event to parent component
    this.fileClick.emit(file);
  }

  onFolderMouseDown(folder: AccordionFolder, event: Event): void {
    // Handle mouse down if needed
  }

  // Method to handle files loaded from backend
  onFilesLoaded(folderId: string, files: FolderFile[]): void {
    // Update pagination state when files are loaded
    this.updatePaginationState(folderId, files.length);

    // Update display folders to reflect the loaded files
    this.updateDisplayFolders();
  }

  // File selection methods
  isFileSelected(folder: AccordionFolder, file: FolderFile): boolean {
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.has(file.fileId) : false;
  }

  toggleFileSelection(folder: AccordionFolder, file: FolderFile, event: MatCheckboxChange): void {
    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    const isCurrentlySelected = selection.has(file.fileId);

    if (isCurrentlySelected) {
      selection.delete(file.fileId);
    } else {
      selection.add(file.fileId);
    }

    // Synchronize selection with INDEX folder (if not already in INDEX)
    if (folder.id !== 'all') {
      this.synchronizeWithIndexFolder(file.fileId, !isCurrentlySelected);
    } else {
      // If we're in INDEX folder, synchronize with the specific folder this file belongs to
      this.synchronizeWithSpecificFolder(file);
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  isAllSelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    return selection ? selection.size === folder.files.length : false;
  }

  isPartiallySelected(folder: AccordionFolder): boolean {
    if (!folder.files || folder.files.length === 0) return false;
    const selection = this.folderSelections.get(folder.id);
    if (!selection) return false;
    return selection.size > 0 && selection.size < folder.files.length;
  }

  masterToggle(folder: AccordionFolder, event: MatCheckboxChange): void {
    // MatCheckboxChange doesn't have stopPropagation, so we don't need to call it

    if (!folder.files || folder.files.length === 0) return;

    let selection = this.folderSelections.get(folder.id);
    if (!selection) {
      selection = new Set();
      this.folderSelections.set(folder.id, selection);
    }

    // Use the event's checked property to determine the action
    const shouldSelectAll = event.checked;

    if (shouldSelectAll) {
      // Select all
      folder.files.forEach(file => {
        selection.add(file.fileId);

        // Synchronize with INDEX folder if not in INDEX
        if (folder.id !== 'all') {
          this.synchronizeWithIndexFolder(file.fileId, true);
        }
      });

      // If we're in INDEX, synchronize with all specific folders
      if (folder.id === 'all') {
        this.synchronizeIndexWithSpecificFolders();
      }
    } else {
      // Deselect all
      selection.clear();

      // Synchronize with INDEX folder if not in INDEX
      if (folder.id !== 'all') {
        folder.files.forEach(file => {
          this.synchronizeWithIndexFolder(file.fileId, false);
        });
      } else {
        // If we're in INDEX, synchronize with all specific folders
        this.synchronizeIndexWithSpecificFolders();
      }
    }

    // Emit selection change
    this.folderSelectionChange.emit({
      folderId: folder.id,
      selectedFiles: this.getSelectedFiles(folder.id)
    });
  }

  onEditFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.editFile.emit({ file, event });
  }

  onDeleteFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.deleteFile.emit({ file, event });
  }

  onCopyFile(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.copyFile.emit({ file, event });
  }

  onGenerateDisc(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.generateDisc.emit({ file, event });
  }

  onAddCommentOrAttachment(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.addCommentOrAttachment.emit({ file, event });
  }

  onSharedIconClick(file: FolderFile, event: Event): void {
    event.stopPropagation();

    // Show the shared users dialog
    const dialogData: SharedUsersDialogData = {
      fileName: file.fileName,
      fileId: file.fileId,
      casename: this.casename,
      sharedUsers: file.sharedUsers || []
    };

    const dialogRef = this.dialog.open(SharedUsersDialogComponent, {
      data: dialogData,
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '85vh',
      disableClose: false,
      panelClass: 'shared-users-dialog-panel'
    });

    // Handle dialog result to update the file's shared users data
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'unshared') {
        // Update the file's sharedUsers array with the remaining users
        file.sharedUsers = result.remainingUsers;

        // Create a new file object with updated sharedUsers to ensure the event has the latest data
        const updatedFile = {
          ...file,
          sharedUsers: result.remainingUsers
        };

        // Emit the updated file for backward compatibility
        this.sharedIconClick.emit(updatedFile);
      } else if (result && result.action === 'closed') {
        // Dialog was closed normally, emit the current file
        this.sharedIconClick.emit(file);
      }
    });

    // Also emit the event for backward compatibility
    this.sharedIconClick.emit(file);
  }

  onFolderNameClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.folderNameClick.emit(file);
  }

  isDicomFile(file: FolderFile): boolean {
    return file.fileName === DICOMDIR_FILE_NAME || file.type === 'dicom';
  }

  isInactiveStore(file: FolderFile): boolean {
    return file.storename === 'inactive' || file.storename === 'Inactive';
  }

  onStudyClick(file: FolderFile, event: Event): void {
    event.stopPropagation();
    this.studyClick.emit(file);
  }

  getSelectedFiles(folderId: string): FolderFile[] {
    const selection = this.folderSelections.get(folderId);
    if (!selection || selection.size === 0) return [];

    const folder = this.accordionFolders.find(f => f.id === folderId);
    if (!folder || !folder.files) return [];

    return folder.files.filter(file => selection.has(file.fileId));
  }

  clearSelection(folderId: string): void {
    this.folderSelections.delete(folderId);
  }

  clearAllSelections(): void {
    this.folderSelections.clear();
  }

  /**
   * Synchronize file selection with INDEX folder
   */
  private synchronizeWithIndexFolder(fileId: string, isSelected: boolean): void {
    let indexSelection = this.folderSelections.get('all');
    if (!indexSelection) {
      indexSelection = new Set();
      this.folderSelections.set('all', indexSelection);
    }

    if (isSelected) {
      indexSelection.add(fileId);
    } else {
      indexSelection.delete(fileId);
    }
  }

  /**
   * Synchronize file selection with the specific folder this file belongs to
   */
  private synchronizeWithSpecificFolder(file: FolderFile): void {
    // Find the specific folder this file belongs to based on parentFolderName or parentFolder
    const targetFolderId = this.findFolderIdByFile(file);
    if (targetFolderId && targetFolderId !== 'all') {
      let targetSelection = this.folderSelections.get(targetFolderId);
      if (!targetSelection) {
        targetSelection = new Set();
        this.folderSelections.set(targetFolderId, targetSelection);
      }

      // Check if file is selected in INDEX
      const indexSelection = this.folderSelections.get('all');
      const isSelectedInIndex = indexSelection ? indexSelection.has(file.fileId) : false;

      if (isSelectedInIndex) {
        targetSelection.add(file.fileId);
      } else {
        targetSelection.delete(file.fileId);
      }
    }
  }

  /**
   * Find the folder ID that contains this file based on parent folder information
   */
  private findFolderIdByFile(file: FolderFile): string | null {
    const parentFolderName = file.parentFolderName || file.parentFolder;
    if (!parentFolderName) return null;

    // Find the folder that matches the parent folder name
    const targetFolder = this.accordionFolders.find(folder => folder.name === parentFolderName && folder.id !== 'all');

    return targetFolder ? targetFolder.id : null;
  }

  /**
   * Synchronize INDEX folder selections with specific folders
   */
  private synchronizeIndexWithSpecificFolders(): void {
    const indexSelection = this.folderSelections.get('all');
    if (!indexSelection) return;

    // Group files by their parent folder
    const filesByFolder = new Map<string, string[]>();

    // Find the INDEX folder
    const indexFolder = this.accordionFolders.find(f => f.id === 'all');
    if (!indexFolder || !indexFolder.files) return;

    indexFolder.files.forEach(file => {
      const folderId = this.findFolderIdByFile(file);
      if (folderId) {
        if (!filesByFolder.has(folderId)) {
          filesByFolder.set(folderId, []);
        }
        filesByFolder.get(folderId)!.push(file.fileId);
      }
    });

    // Update each specific folder's selection
    filesByFolder.forEach((fileIds, folderId) => {
      let folderSelection = this.folderSelections.get(folderId);
      if (!folderSelection) {
        folderSelection = new Set();
        this.folderSelections.set(folderId, folderSelection);
      }

      fileIds.forEach(fileId => {
        if (indexSelection.has(fileId)) {
          folderSelection.add(fileId);
        } else {
          folderSelection.delete(fileId);
        }
      });
    });
  }

  trackByFileId(index: number, file: FolderFile): string {
    return file.fileId || index.toString();
  }

  trackByFolderId(index: number, folder: AccordionFolder): string {
    return folder.id || index.toString();
  }

  /**
   * Format date string to a readable format
   * @param dateString - The date string to format
   * @returns Formatted date string or empty string if invalid
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';

    try {
      // Handle various date formats
      let date: Date;

      // Check if it's already a formatted date string
      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        // ISO format: YYYY-MM-DD
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        // US format: MM/DD/YYYY
        date = new Date(dateString);
      } else {
        // Try parsing as is
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Format date and time string to a readable format
   * @param dateString - The date string to format
   * @returns Formatted date and time string or empty string if invalid
   */
  formatDateTime(dateString: string): string {
    if (!dateString) return '';

    try {
      // Handle various date formats
      let date: Date;

      // Check if it's already a formatted date string
      if (dateString.includes('GMT') || dateString.includes('UTC')) {
        date = new Date(dateString);
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
        // ISO format: YYYY-MM-DD
        date = new Date(dateString);
      } else if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
        // US format: MM/DD/YYYY
        date = new Date(dateString);
      } else {
        // Try parsing as is
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Format file date (fdate) and time (ftime) combination
   * @param fdate - The file date
   * @param ftime - The file time
   * @returns Formatted date and time string or empty string if invalid
   */
  formatFileDateTime(fdate: string, ftime: string): string {
    if (!fdate) return '';

    try {
      // If we have both date and time, combine them
      if (ftime) {
        const dateTimeString = `${fdate} ${ftime}`;
        const date = new Date(dateTimeString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // If only date is available
      const date = new Date(fdate);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  // Removed search-related methods - not needed for HUB-only functionality

  // Simplified hierarchical folder display for HUB-only functionality
  private updateDisplayFolders(): void {
    const allFolders = this.accordionDataService.getCurrentFolders();
    const showOnlyHubs = !allFolders.some(folder => folder.type === 'hub' && folder.expanded);
    const foldersToShow = showOnlyHubs
      ? allFolders.filter(folder => folder.type === 'hub')
      : this.filterFoldersForDisplay(allFolders);
    this.displayFolders = this.buildHierarchicalDisplay(foldersToShow);
  }

  /**
   * Filter folders for display - hide regular folders that are children of expanded hub folders
   * @param folders All folders from the accordion data service
   * @returns Filtered folders for display
   */
  private filterFoldersForDisplay(folders: AccordionFolder[]): AccordionFolder[] {
    // Find expanded hub folders
    const expandedHubFolders = folders.filter(folder => folder.type === 'hub' && folder.expanded);

    if (expandedHubFolders.length === 0) {
      // No hub is expanded, show all folders
      return folders;
    }

    // Map for quick parent traversal
    const idToFolder = new Map<string, AccordionFolder>();
    folders.forEach(f => {
      if (f && f.id) {
        idToFolder.set(f.id, f);
      }
    });

    const expandedHubIds = new Set(expandedHubFolders.map(h => h.id));

    const isDescendantOfExpandedHub = (folder: AccordionFolder): boolean => {
      let current: AccordionFolder | undefined = folder;
      // Walk up the chain to see if any ancestor is an expanded hub
      while (current && current.parentId) {
        const parent = idToFolder.get(current.parentId);
        if (!parent) break;
        if (expandedHubIds.has(parent.id) && parent.type === 'hub') {
          return true;
        }
        current = parent;
      }
      return false;
    };

    // Show hubs, search results, and any descendant of an expanded hub
    const filteredFolders = folders.filter(folder => {
      if (folder.type === 'hub' || folder.type === 'search-results') {
        return true;
      }
      return isDescendantOfExpandedHub(folder);
    });

    return filteredFolders;
  }

  // Removed syncExpansionStates method - not needed for HUB-only functionality

  private buildHierarchicalDisplay(folders: AccordionFolder[]): AccordionFolder[] {
    // First, flatten any existing children into the main folders list
    const allFolders: AccordionFolder[] = [];
    const processedIds = new Set<string>();

    const flattenFolder = (folder: AccordionFolder) => {
      if (processedIds.has(folder.id)) {
        return; // Skip duplicates
      }
      processedIds.add(folder.id);
      allFolders.push(folder);

      // Process children recursively
      if (folder.children && folder.children.length > 0) {
        folder.children.forEach(child => flattenFolder(child));
      }
    };

    folders.forEach(folder => flattenFolder(folder));

    // Create a map for quick lookup
    const folderMap = new Map<string, AccordionFolder>();
    const rootFolders: AccordionFolder[] = [];

    // First pass: create map and identify root folders
    allFolders.forEach(folder => {
      // Create a deep copy to avoid modifying the original, but reset children
      const folderCopy = { ...folder, children: [] };
      folderMap.set(folder.id, folderCopy);

      // A folder is a root folder if it has no parentId/parentFolder OR if it's a hub folder
      // Handle both parentId and parentFolder properties (parentFolder comes from clientprofile-spoke)
      const parentId = folder.parentId || (folder as any).parentFolder;
      if (!parentId || parentId === null || parentId === '' || folder.type === 'hub') {
        rootFolders.push(folderCopy);
      }
    });

    // Second pass: build children arrays for unlimited depth
    allFolders.forEach(folder => {
      // Check both parentId and parentFolder properties
      // Handle both parentId and parentFolder properties (parentFolder comes from clientprofile-spoke)
      const parentId = folder.parentId || (folder as any).parentFolder;
      if (parentId && parentId !== null && parentId !== '') {
        const parent = folderMap.get(parentId);
        if (parent) {
          // Add children to parent folders at any level
          if (!parent.children) {
            parent.children = [];
          }
          // Find the child folder copy in the map
          const childCopy = folderMap.get(folder.id);
          if (childCopy && !parent.children.some(child => child.id === childCopy.id)) {
            parent.children.push(childCopy);
          }
        }
      }
    });

    // Third pass: recursively set levels for all folders in the hierarchy
    const setLevelsRecursively = (folderList: AccordionFolder[], level: number = 0) => {
      folderList.forEach(folder => {
        folder.level = level;
        if (folder.children && folder.children.length > 0) {
          setLevelsRecursively(folder.children, level + 1);
        }
      });
    };

    setLevelsRecursively(rootFolders);

    return rootFolders;
  }

  getFolderIndent(folder: AccordionFolder): string {
    // Get the level from the folder's level property
    const level = folder.level || 0;
    // Only apply indentation for level 1+ folders (children)
    // Level 0 folders (root folders) should not be indented
    return level > 0 ? `${level * 20}px` : '0px';
  }

  // When a hub is expanded and it contains exactly one root child folder,
  // we render that child's content directly (no child header row)
  // DISABLED: Always show children as expandable folders to allow access to subfolders
  isSingleChildMode(folder: AccordionFolder): boolean {
    // Always return false to ensure children are shown as expandable folders
    // This allows users to see the folder structure and access subfolders
    return false;
  }

  getSingleChild(folder: AccordionFolder): AccordionFolder | null {
    if (!this.isSingleChildMode(folder)) {
      return null;
    }
    const children = (folder.children || []).filter(
      child => child && child.type !== 'hub' && child.parentId === folder.id
    );
    return children.length === 1 ? children[0] : null;
  }

  hasChildren(folder: AccordionFolder): boolean {
    // Check if this folder has children by looking at the folder's children array
    if (folder.children && folder.children.length > 0) {
      return true;
    }
    // Fallback to searching in display folders
    return this.displayFolders.some(f => f.parentId === folder.id);
  }

  getChildren(folder: AccordionFolder): AccordionFolder[] {
    // Get children from the folder's children array
    if (folder.children && folder.children.length > 0) {
      return folder.children;
    }
    // Fallback to searching in display folders
    return this.displayFolders.filter(f => f.parentId === folder.id);
  }

  toggleChildrenVisibility(folder: AccordionFolder): void {
    // This method is called when a folder with children is clicked
    // The expansion state is already handled in onFolderClick
    // This method can be used for additional child-specific logic if needed
  }

  isChildrenExpanded(folder: AccordionFolder): boolean {
    // For folders with children, check if any of their children are visible
    // This is different from the folder's own expansion state
    const children = this.getChildren(folder);
    return (
      children.length > 0 &&
      children.some(child => {
        // Check if the child is expanded in the display folders
        return child.expanded || false;
      })
    );
  }

  /**
   * Check if a folder is a predefined folder that should not be deleted
   * @param folder The folder to check
   * @returns true if the folder should not be deleted, false otherwise
   */
  isPredefinedFolder(folder: AccordionFolder): boolean {
    // INDEX folder (identified by name or id)
    if (folder.name === 'INDEX' || folder.id === 'all') {
      return true;
    }

    // Search results folder
    if (folder.type === 'search-results') {
      return true;
    }

    // Add other predefined folder checks here as needed
    // For example, if there are other system folders that shouldn't be deleted

    return false;
  }

  onDeleteFolder(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    // Emit delete folder event to parent component
    this.deleteFolder.emit({
      folder: folder,
      event: event
    });
  }

  // Subfolder expansion methods
  isSubfolderExpanded(subfolder: AccordionFolder): boolean {
    return this.subfolderExpansionStates.get(subfolder.id) || false;
  }

  toggleSubfolderExpansion(subfolder: AccordionFolder, event: Event): void {
    event.stopPropagation();

    const currentState = this.subfolderExpansionStates.get(subfolder.id) || false;
    this.subfolderExpansionStates.set(subfolder.id, !currentState);

    // If expanding and no files loaded yet, load from cache or backend
    if (!currentState && !subfolder.files && !subfolder.loading) {
      // First try to get files from cache
      const cachedFiles = this.accordionDataService.getCachedFiles(subfolder.id);

      if (cachedFiles) {
        // Use cached files
        this.accordionDataService.updateFolderFiles(subfolder.id, cachedFiles);
        // Update pagination state for cached files
        this.updatePaginationState(subfolder.id, cachedFiles.length);
      } else {
        // Load from backend if not in cache
        this.accordionDataService.setFolderLoading(subfolder.id, true);
        this.loadFolderContents.emit(subfolder.id);
      }
    }
  }

  onSubfolderClick(subfolder: AccordionFolder, event: Event): void {
    event.stopPropagation();
    // The actual expansion is handled by the expand button
    // This method can be used for additional subfolder click logic if needed
  }

  /**
   * Check if a folder can have subfolders
   * @param folder The folder to check
   * @returns false - subfolder creation is disabled in this component
   */
  canHaveSubfolders(folder: AccordionFolder): boolean {
    // Subfolder creation is disabled
    return false;
  }

  /**
   * Check if a folder is at the maximum allowed depth (prevents grandchild folders)
   * @param folder The folder to check
   * @returns true if the folder is at maximum depth, false otherwise
   */
  isAtMaxDepth(folder: AccordionFolder): boolean {
    // Maximum depth is 2 (root -> subfolder -> grandchild)
    return (folder.level || 0) >= 2;
  }

  /**
   * Check if a folder should be visible based on the selected hub's facility
   * @param folder The folder to check
   * @returns true if the folder should be visible, false otherwise
   */
  isFolderVisibleForSelectedHub(folder: AccordionFolder): boolean {
    // If no hub is selected, show all folders
    if (!this.selectedHub) {
      return true;
    }

    // Hub folders should always be visible
    if (folder.type === 'hub') {
      return true;
    }

    // Use the correct property name from Firebase (restricted_to, not restrictedTo)
    const restrictedTo = folder.restricted_to || folder.restrictedTo;

    // If folder has no restrictions, show it
    if (!restrictedTo || restrictedTo.length === 0) {
      return true;
    }

    // Get the current facility from the selected hub
    const currentFacility = this.getCurrentFacilityFromHub();
    if (!currentFacility) {
      return true; // If we can't determine facility, show all folders
    }

    // Check if the folder's restricted_to includes the current facility
    const isVisible = restrictedTo.some(restriction => {
      // Handle different restriction formats
      if (typeof restriction === 'string') {
        return restriction === currentFacility;
      } else if (typeof restriction === 'object' && restriction.facility) {
        return restriction.facility === currentFacility;
      }
      return false;
    });

    return isVisible;
  }

  /**
   * Get the current facility from the selected hub
   * @returns The facility identifier or null if not found
   */
  private getCurrentFacilityFromHub(): string | null {
    if (!this.selectedHub) {
      return null;
    }

    // If selectedHub is a string (hub name), use it as the facility
    if (typeof this.selectedHub === 'string') {
      return this.selectedHub;
    }

    // If selectedHub is an object, try to extract facility information
    if (this.selectedHub.facility) {
      return this.selectedHub.facility;
    }

    if (this.selectedHub.facilityId) {
      return this.selectedHub.facilityId;
    }

    if (this.selectedHub.facilityName) {
      return this.selectedHub.facilityName;
    }

    // If we can't determine the facility, return null
    return null;
  }

  /**
   * Filter folders based on the selected hub's facility
   * @param folders The folders to filter
   * @returns Filtered folders that are visible for the selected hub
   */
  private filterFoldersByFacility(folders: AccordionFolder[]): AccordionFolder[] {
    return folders.filter(folder => this.isFolderVisibleForSelectedHub(folder));
  }

  /**
   * Apply facility filtering to the current folders (simplified for HUB-only)
   */
  private applyFacilityFiltering(): void {
    // For HUB-only functionality, just ensure hub folders are present
    this.ensureHubFoldersPresent();
    this.updateDisplayFolders();
  }

  /**
   * Get the selected tab index for a hub folder
   * @param folderId The hub folder ID
   * @returns The selected tab index (defaults to 0)
   */
  getSelectedTabIndex(folderId: string): number {
    return this.tabSelectionStates.get(folderId) || 0;
  }

  onTabClick(child: AccordionFolder, event: Event): void {
    event.stopPropagation();
    console.log('child: ', child);
  }

  onTabGroupClick(folder: AccordionFolder, event: Event): void {
    event.stopPropagation();
    console.log('folder: ', folder);
  }

  /**
   * Handle tab change event
   * @param folderId The hub folder ID
   * @param event The tab change event
   */
  onTabChange(folderId: string, event: any): void {
    const selectedIndex = event.index;
    console.log('selectedIndex: ', selectedIndex);
    this.tabSelectionStates.set(folderId, selectedIndex);

    // Get the hub folder and its children
    const hubFolder = this.accordionDataService.getCurrentFolders().find(f => f.id === folderId);

    if (hubFolder && hubFolder.children && hubFolder.children.length > selectedIndex) {
      const selectedChild = hubFolder.children[selectedIndex];
      const folderName = selectedChild.name;

      if (selectedChild.upload_folder === true) {
        this.titleText = 'Upload files to share with';
      } else {
        this.titleText = 'Shared files from';
      }

      this.selectedHub = folderName;

      // Load files for the selected child if not already loaded
      if (!selectedChild.files && !selectedChild.loading) {
        const cachedFiles = this.accordionDataService.getCachedFiles(selectedChild.id);
        if (cachedFiles) {
          this.accordionDataService.updateFolderFiles(selectedChild.id, cachedFiles);
          this.updatePaginationState(selectedChild.id, cachedFiles.length);
        } else {
          this.accordionDataService.setFolderLoading(selectedChild.id, true);
          this.loadFolderContents.emit(selectedChild.id);
        }
      }
    }
  }

  /**
   * Get the tab label for a child folder
   * @param child The child folder
   * @returns The tab label with count if available
   */
  getTabLabel(child: AccordionFolder): string {
    let label = child.name;

    // Add count if available
    if (child.count && child.count !== '') {
      label += ` (${child.count})`;
    }

    return label;
  }
}
